import React, { useEffect, useMemo, useState } from 'react';
import { storesApi, usersApi } from '../services/api';

const ROLE_OPTIONS = [
    { value: 'cashier', label: 'Sales Staff (Cashier)' },
    { value: 'staff', label: 'Sales Assistant' },
];

function initialForm() {
    return {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        role: 'cashier',
        store_id: '',
    };
}

export default function StaffManagementPage() {
    const [users, setUsers] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(initialForm());

    const load = async () => {
        setLoading(true);
        const [usersRes, storesRes] = await Promise.all([
            usersApi.list({ limit: 300 }),
            storesApi.list({ limit: 300 }),
        ]);
        const userRows = usersRes?.data?.data || [];
        const storeRows = storesRes?.data?.data || [];
        setUsers(userRows);
        setStores(storeRows);
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const salesUsers = useMemo(
        () => users.filter((item) => ['cashier', 'staff'].includes(item.role)),
        [users]
    );

    const saveUser = async (event) => {
        event.preventDefault();
        if (!form.first_name || !form.last_name || !form.email || !form.password) {
            window.alert('First name, last name, email and password are required.');
            return;
        }

        setSaving(true);
        const response = await usersApi.create({
            ...form,
            store_id: form.store_id ? Number(form.store_id) : null,
        });
        setSaving(false);

        if (!response.success) {
            window.alert(response.error || 'Could not create sales staff account');
            return;
        }

        setForm(initialForm());
        load();
    };

    const toggleStatus = async (user) => {
        const nextStatus = user.status === 'active' ? 'inactive' : 'active';
        const response = await usersApi.update(user.id, { status: nextStatus });
        if (!response.success) {
            window.alert(response.error || 'Could not update user status');
            return;
        }
        load();
    };

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Sales Staff Accounts</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> Sales Staff</p>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="panel stock-panel">
                    <h3>Add Sales Staff Login</h3>
                    <form className="stock-form-grid" onSubmit={saveUser}>
                        <label>
                            <span>First Name</span>
                            <input value={form.first_name} onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Last Name</span>
                            <input value={form.last_name} onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Email (login username)</span>
                            <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Phone</span>
                            <input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} />
                        </label>
                        <label>
                            <span>Password</span>
                            <input type="password" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Role</span>
                            <select value={form.role} onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}>
                                {ROLE_OPTIONS.map((role) => (
                                    <option key={role.value} value={role.value}>{role.label}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Default Store / Warehouse</span>
                            <select value={form.store_id} onChange={(e) => setForm((prev) => ({ ...prev, store_id: e.target.value }))}>
                                <option value="">Unassigned</option>
                                {stores.map((store) => (
                                    <option key={store.id} value={store.id}>{store.name} ({store.store_type || 'store'})</option>
                                ))}
                            </select>
                        </label>
                        <div className="stock-form-actions">
                            <button className="btn" type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Sales Staff'}</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <h3>Sales Staff List</h3>
                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Store</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="dash-empty-row">Loading...</td></tr>
                                ) : salesUsers.length === 0 ? (
                                    <tr><td colSpan={6} className="dash-empty-row">No sales staff accounts yet</td></tr>
                                ) : salesUsers.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.first_name} {item.last_name}</td>
                                        <td>{item.email}</td>
                                        <td>{item.role}</td>
                                        <td>{item.status}</td>
                                        <td>{stores.find((store) => String(store.id) === String(item.store_id))?.name || 'Unassigned'}</td>
                                        <td>
                                            <button className="btn btn-sm" type="button" onClick={() => toggleStatus(item)}>
                                                {item.status === 'active' ? 'Deactivate' : 'Activate'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}
